/* eslint-disable */
import React, { useState, useEffect } from 'react'
import { Form, Input, Select, Button } from 'antd'
import { useSelector, useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { addBankAccount, editBankAccount } from '../../../core/redux/bankAccount.slice'
import { fetchCustomers } from '../../../core/redux/customer.slice'
import { fetchSuppliers } from '../../../core/redux/supplier.slice'
import { fetchActiveCompany } from '../../../core/redux/company.slice'
import { useTranslation } from "react-i18next";
const { Option } = Select

const AccountListModal = ({
  onCancel,
  isEdit = false,
  initialData = null,
  loading = false,
  isReadMode = false,
}) => {
  const [form] = Form.useForm()
  const dispatch = useDispatch()

  const { customers } = useSelector((state) => state.customer)
  const { suppliers } = useSelector((state) => state.supplier)
  const { company: activeCompany } = useSelector((state) => state.company)

  const [customerOptions, setCustomerOptions] = useState([])
  const [supplierOptions, setSupplierOptions] = useState([])
  const [accountType, setAccountType] = useState(null)
  const { t } = useTranslation();

  // Chargement clients / fournisseurs — runs once when this component mounts
  useEffect(() => {
    dispatch(fetchCustomers())
    dispatch(fetchSuppliers())
  }, [dispatch])

  // Chargement société active
  useEffect(() => {
    if (!activeCompany) {
      dispatch(fetchActiveCompany())
    }
  }, [activeCompany, dispatch])

  useEffect(() => {
    if (customers.length) {
      setCustomerOptions(
        customers
          .filter((c) => c.customerId != null)
          .map((customer) => ({
            label: customer.customerName,
            value: customer.customerId,
          })),
      )
    } else {
      setCustomerOptions([])
    }
  }, [customers])

  useEffect(() => {
    if (suppliers.length) {
      setSupplierOptions(
        suppliers
          .filter((s) => s.supplierId != null)
          .map((supplier) => ({
            label: supplier.supplierName,
            value: supplier.supplierId,
          })),
      )
    } else {
      setSupplierOptions([])
    }
  }, [suppliers])

  // Init form (edit / read) — runs once on mount since initialData/isEdit/isReadMode
  // are fixed for the lifetime of this component instance now
  useEffect(() => {
    if ((isEdit || isReadMode) && initialData) {
      let type = null
      let holderId = null

      if (initialData.companyId) {
        type = 'company'
        holderId = initialData.companyId
      } else if (initialData.customerId) {
        type = 'customer'
        holderId = initialData.customerId
      } else if (initialData.supplierId) {
        type = 'supplier'
        holderId = initialData.supplierId
      }

      setAccountType(type)

      form.setFieldsValue({
        accountType: type || undefined,
        holderId: holderId || undefined,
        rib: initialData.rib,
        iban: initialData.iban,
        bankName: initialData.bankName,
        bankAgency: initialData.bankAgency,
        bic: initialData.bic,
        bankAccountIsActive:
          typeof initialData.bankAccountIsActive === 'boolean'
            ? initialData.bankAccountIsActive
            : true,
      })
    } else {
      form.resetFields()
      setAccountType(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleAccountTypeChange = (value) => {
    setAccountType(value)
    form.setFieldsValue({ holderId: undefined })
  }

  const getHolderOptions = () => {
    if (accountType === 'company') {
      if (!activeCompany) return []

      const companyId = activeCompany.id || activeCompany.companyId
      const companyName = activeCompany.companyName || activeCompany.name || 'Société'

      if (!companyId) return []

      return [
        {
          label: companyName,
          value: companyId,
        },
      ]
    }

    if (accountType === 'customer') return customerOptions
    if (accountType === 'supplier') return supplierOptions
    return []
  }

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const accountData = {
          rib: values.rib,
          iban: values.iban,
          bankName: values.bankName,
          bankAgency: values.bankAgency,
          bic: values.bic || '',
          bankAccountIsActive:
            typeof values.bankAccountIsActive === 'boolean'
              ? values.bankAccountIsActive
              : values.bankAccountIsActive === false
                ? false
                : true,
          companyId: values.accountType === 'company' ? values.holderId : null,
          customerId: values.accountType === 'customer' ? values.holderId : null,
          supplierId: values.accountType === 'supplier' ? values.holderId : null,
        }

        if (isEdit && !isReadMode && initialData?.accountId) {
          dispatch(editBankAccount({ id: initialData.accountId, bankAccount: accountData }))
            .unwrap()
            .then(() => {
              form.resetFields()
              setAccountType(null)
              onCancel()
            })
            .catch((error) => {
              console.error('Error updating account:', error)
            })
        } else if (!isEdit) {
          dispatch(addBankAccount(accountData))
            .unwrap()
            .then(() => {
              form.resetFields()
              setAccountType(null)
              onCancel()
            })
            .catch((error) => {
              console.error('Error creating account:', error)
            })
        }
      })
      .catch((err) => {
        console.log('VALIDATION ERROR =>', err)
      })
  }

  const handleCancel = () => {
    form.resetFields()
    setAccountType(null)
    onCancel()
  }

  const panelTitle = isReadMode
    ? t("accountList.modal.detailsTitle")
    : isEdit
      ? t("accountList.modal.editTitle")
      : t("accountList.modal.addTitle");
  return (
    <div className="card table-list-card">
      <div
        className="d-flex align-items-center justify-content-between px-4 py-3"
        style={{ background:'var(--primary-gradient)', borderRadius: '8px 8px 0 0' }}
      >
        <h4 className="text-white mb-0">{panelTitle}</h4>
        <div className="d-flex gap-2">
          {isReadMode ? (
            <Button onClick={handleCancel}>  {t("common.close")}</Button>
          ) : (
            <>
              <Button onClick={handleCancel}> {t("common.cancel")}</Button>
              <Button type="primary" loading={loading} onClick={handleOk}>
                {isEdit
                  ? t("accountList.modal.edit")
                  : t("accountList.modal.add")}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="card-body p-4">
        <Form form={form} layout="vertical" disabled={isReadMode}>
          <Form.Item
            name="accountType"
            label={t("accountList.modal.fields.accountType")}
            rules={[{ required: true, message:t("accountList.modal.validation.selectType")}]}
          >
            <Select placeholder={t("accountList.modal.placeholders.selectType")} onChange={handleAccountTypeChange}>
              <Option key="type-company" value="company">{t("generalSettings.title")}</Option>
              <Option key="type-customer" value="customer">{t("common.customer")}</Option>
              <Option key="type-supplier" value="supplier">{t("common.supplier")}</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="holderId"
            label={t("accountList.modal.fields.holder")}
            rules={[{ required: true, message: t("accountList.modal.validation.selectHolder") }]}
          >
            <Select
              placeholder={t("accountList.modal.placeholders.selectHolder")}
              disabled={!accountType}
              showSearch
              filterOption={(input, option) =>
                option?.children?.toLowerCase?.().indexOf(input.toLowerCase()) >= 0
              }
            >
              {getHolderOptions().map((option) => (
                <Option key={`holder-${option.value}`} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="rib" label={t("accountList.modal.fields.rib")} rules={[{ required: true, message:t("accountList.modal.validation.ribRequired") }]}>
            <Input placeholder={t("accountList.modal.placeholders.rib")} />
          </Form.Item>

          <Form.Item name="iban" label={t("accountList.modal.fields.iban")} rules={[{ required: true, message:t("accountList.modal.validation.ibanRequired") }]}>
            <Input placeholder={t("accountList.modal.placeholders.iban")} />
          </Form.Item>

          <Form.Item name="bankName" label={t("accountList.modal.fields.bankName")} rules={[{ required: true, message:t("accountList.modal.validation.bankRequired") }]}>
            <Input placeholder={t("accountList.modal.placeholders.bankName")} />
          </Form.Item>

          <Form.Item name="bankAgency" label={t("accountList.modal.fields.bankAgency")} rules={[{ required: true, message:t("accountList.modal.validation.agencyRequired")}]}>
            <Input placeholder={t("accountList.modal.placeholders.bankAgency")} />
          </Form.Item>

          <Form.Item name="bic" label={t("accountList.modal.fields.bic")}>
            <Input placeholder={t("accountList.modal.placeholders.bic")} />
          </Form.Item>

          <Form.Item name="bankAccountIsActive" label={t("accountList.modal.fields.status")} initialValue={true}>
            <Select>
              <Option key="status-true" value={true}>{t("accountList.modal.status.active")}</Option>
              <Option key="status-false" value={false}>{t("accountList.modal.status.inactive")}</Option>
            </Select>
          </Form.Item>
        </Form>
      </div>
    </div>
  )
}

AccountListModal.propTypes = {
  onCancel: PropTypes.func.isRequired,
  isEdit: PropTypes.bool,
  initialData: PropTypes.shape({
    accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    rib: PropTypes.string,
    iban: PropTypes.string,
    bankName: PropTypes.string,
    bankAgency: PropTypes.string,
    bic: PropTypes.string,
    bankAccountIsActive: PropTypes.bool,
    companyId: PropTypes.string,
    customerId: PropTypes.string,
    supplierId: PropTypes.string,
  }),
  loading: PropTypes.bool,
  isReadMode: PropTypes.bool,
}

export default AccountListModal